import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  RowSelectionState,
  ColumnFiltersState
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper
} from '@mui/material';
import { useEmployeeSelectionColumns } from './columns';
import { useListEmployees } from '../../../../queries/employee';
import { useStore } from '../../../../hooks/useStore';
import { useState, useEffect, useMemo } from 'react';
import { globalFilterFn } from '../../../util/filter';
import { useParams } from 'react-router';
import {
  CreateEmployeeExportRequest,
  CreatePayrollExportRequest,
  CreateReceiptExportRequest,
  EmployeeListItem
} from '@pxtra-client-lib-legacy/gen';
import { TableToolBar } from '../shared/toolbar';
import Pagination from '../shared/pagination';
import { DEFAULT_PAGE_SIZE, scrollableTableContainerSx } from '../shared/utils';
import { exportFlows } from '../../../../pages/exports/config';
import { observer } from 'mobx-react-lite';
import { DataTableLoadingBar } from '../shared/loading-bar';
import EmptyTable from '../../../icons/svg/empty-table.svg';
import { useTranslation } from 'react-i18next';
import { SortableTableHeader } from '../shared/sortable-header';
import { enqueueSnackbar } from 'notistack';

export const EmployeeExportSelectionTable = observer(() => {
  const { companyStore, exportFlowStore } = useStore();
  const params = useParams();
  const { t } = useTranslation();

  const { data, isFetching } = useListEmployees(companyStore.company?.id, {
    enabled: !!companyStore.company?.id // wait until company is available
  });

  const employeesData = useMemo(() => data?.pages?.flatMap((page) => page) ?? [], [data]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(() => {
    const selectedIds =
      (
        exportFlowStore.data as
          CreateEmployeeExportRequest | CreateReceiptExportRequest | CreatePayrollExportRequest
      )?.employees_query?.ids || [];
    return selectedIds.reduce((acc, id) => ({ ...acc, [id]: true }), {});
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const columns = useEmployeeSelectionColumns();

  useEffect(() => {
    if (params.type && !exportFlowStore.getIsInitialized(params.type as keyof typeof exportFlows)) {
      exportFlowStore.initializeFlow(params.type as keyof typeof exportFlows);
    }
  }, [params.type]);

  const onRowSelectionChange = (
    updaterOrValue: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)
  ) => {
    let updatedSelection =
      typeof updaterOrValue === 'function' ? updaterOrValue(rowSelection) : updaterOrValue;

    let selectedIds = Object.keys(updatedSelection);

    if (exportFlowStore.currentType === 'payroll_data') {
      const validEmployees = selectedIds
        .map((id) => employeesData.find((emp) => emp.id === id))
        .filter((emp): emp is EmployeeListItem => !!emp && !emp.has_missing_fields);

      const validIds = validEmployees.map((emp) => emp.id!);

      updatedSelection = Object.fromEntries(validIds.map((id) => [id, true]));

      if (validIds.length !== selectedIds.length) {
        selectedIds = validIds;
        enqueueSnackbar(t('export:WARNING_INCOMPLETE_EMPLOYEES'), { variant: 'warning' });
      }
    }

    setRowSelection(updatedSelection);

    exportFlowStore.updateData({ employees_query: { ids: selectedIds } });
  };

  const table = useReactTable({
    data: employeesData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    globalFilterFn: globalFilterFn,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange,
    getRowId: (row) => row.id ?? 'MISSING_ROW_ID',
    onColumnFiltersChange: setColumnFilters,
    state: {
      pagination,
      globalFilter,
      rowSelection,
      columnFilters
    },
    initialState: {
      columnVisibility: {
        member_groups: false
      }
    }
  });

  return (
    <Paper className="overflow-hidden flex flex-col">
      <TableToolBar table={table} />
      <TableContainer sx={scrollableTableContainerSx('calc(100vh - 500px)')}>
        <Table stickyHeader className="bg-white">
          <TableHead sx={{ position: 'sticky', top: 0, zIndex: 2 }}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <SortableTableHeader
                    key={header.id}
                    header={header}
                    isLoading={isFetching}
                    hasData={employeesData.length > 0}
                  />
                ))}
              </TableRow>
            ))}
            <TableRow>
              <TableCell
                sx={{
                  padding: 0,
                  borderBottom: 'none',
                  backgroundColor: 'transparent'
                }}
                colSpan={columns.length}
              >
                <DataTableLoadingBar isLoading={isFetching} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="!border-none">
                  <div className="flex flex-col items-center justify-center">
                    <img
                      src={EmptyTable}
                      alt={t('export:EMPTY_EMPLOYEE_SELECTION_TABLE')}
                      className="w-64 h-64"
                    />
                    <p className={'mt-4 text-grey-500'}>
                      {t('export:EMPTY_EMPLOYEE_SELECTION_TABLE')}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell className="py-2 border-grey-100" key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination table={table} />
    </Paper>
  );
});
